import { useDispatch, useSelector } from 'react-redux'
import { Alert, Box, Button, Dialog, Divider, Paper, Stack, TextField, Typography } from '@mui/material'
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined'
import type { RootState } from '../../store'
import { atualizarQuantidade, definirObservacaoGeral, definirObservacaoItem, duplicarItem, removerItem, } from '../../store/carrinho-slice'
import { formatarBRL } from '../../lib/formato'
import { canalDoIdentificador, usaTecladoDeTela } from '../../lib/canal'
import { useImagensPorProduto, useTiposCalculoPorPasso } from '../../lib/imagens-catalogo'
import { itensCarrinhoParaListagem } from '../../lib/itens-pedido'
import { ListaItensPedido } from '../../components/lista-itens-pedido'
import { EstadoVazio } from '../../components/estado-mensagem'
import { useGetLojaAtualQuery } from '../../api'
import useMediaQuery from '@mui/material/useMediaQuery'
import { ESTILO_ACAO_KIOSK, TemaKiosk } from '../../components/catalogo-kiosk/tema-kiosk'
import { avisoDeMudancaDePreco, type AlteracaoPreco } from '../../lib/reconciliar-carrinho'
import ScreenHeader from '../totem/checkout/components/screen-header'
import { medidasDoAparelho } from '../catalogo/kiosk/medidas'
import AddIcon from '@mui/icons-material/Add'
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart'

interface CarrinhoKioskProps {
  bloqueado: boolean
  alteracoes?: AlteracaoPreco[]
  aoDescartarAviso?: () => void
  aoVoltar: () => void
  aoFinalizar: () => void
}

export function CarrinhoKiosk({ bloqueado, alteracoes = [], aoDescartarAviso, aoVoltar, aoFinalizar }: CarrinhoKioskProps): JSX.Element {
  const aviso = avisoDeMudancaDePreco(alteracoes, formatarBRL)
  const dispatch = useDispatch()
  const { itens, observacaoGeral } = useSelector((s: RootState) => s.carrinho)
  const { data: loja } = useGetLojaAtualQuery()
  const canal = loja ? canalDoIdentificador(loja.identificador) : 'CARDAPIO_TOTEM'
  const imagens = useImagensPorProduto(canal)
  const tiposCalculo = useTiposCalculoPorPasso(canal)
  const itensListados = itensCarrinhoParaListagem(itens, imagens, tiposCalculo)
  const tecladoProprio = !!loja && usaTecladoDeTela(loja.identificador)
  const subtotal = itens.reduce((s, item) => s + item.precoUnitario * item.quantidade, 0)
  const totalItens = itens.reduce((s, item) => s + item.quantidade, 0)
  const vazio = itens.length === 0
  const voltar = aoVoltar
  const deitado = useMediaQuery('(orientation: landscape) and (min-width: 820px)')

  return (
    <TemaKiosk>
      <Dialog
        open
        fullScreen
        onClose={voltar}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'background.default',
              userSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              '& input, & textarea': { userSelect: 'text' },
            },
          }
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <ScreenHeader
            title="Seu pedido"
            subTitle={`${totalItens} ${totalItens === 1 ? 'item' : 'itens'}`}
            logoUrl={loja?.configuracao.urlLogo}
            alturaLogo={loja ? medidasDoAparelho(loja.identificador).alturaLogo : undefined}
            fonteTitulo={loja ? medidasDoAparelho(loja.identificador).fonteTitulo : undefined}
            fonteSubtitulo={loja ? medidasDoAparelho(loja.identificador).fonteSubtitulo : undefined}
            onClickBack={voltar}
          />
        </Box>

        <Stack sx={{ height: '100%', overflow: 'hidden' }}>
          {vazio ? (
            <EstadoVazio
              tamanho="grande"
              icone={<ShoppingCartOutlinedIcon />}
              titulo="Seu pedido está vazio"
              descricao="Escolha os itens no cardápio para começar."
              acao={
                <Button variant="contained" size="large" onClick={voltar} sx={{ py: 1.5, px: 4, fontSize: '1.1rem' }}>
                  Voltar ao cardápio
                </Button>
              }
            />
          ) : (
            <Stack direction={deitado ? 'row' : 'column'} sx={{ flexGrow: 1, overflow: 'hidden' }}>
              <Stack
                sx={{
                  flexGrow: 1,
                  px: 2,
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(120, 130, 140, .45) transparent',
                  '&::-webkit-scrollbar': { width: 8 },
                  '&::-webkit-scrollbar-thumb': { borderRadius: 8, backgroundColor: 'rgba(120, 130, 140, .45)' },
                }}
              >
                {aviso && (
                  <Alert severity={aviso.tom} onClose={aoDescartarAviso} sx={{ mt: 2, fontSize: '1.05rem' }}>
                    {aviso.texto}
                  </Alert>
                )}

                <Box sx={{ mt: -1.5 }}>
                  <ListaItensPedido
                    tamanho="grande"
                    itens={itensListados}
                    tecladoProprio={tecladoProprio}
                    acoes={{
                      minimoUm: true,
                      aoAlterarQuantidade: (id, delta) => {
                        const item = itens.find((i) => i.id === id)
                        if (item) dispatch(atualizarQuantidade({ id, quantidade: item.quantidade + delta }))
                      },
                      aoDuplicar: (id) => dispatch(duplicarItem({ id })),
                      aoRemover: (id) => dispatch(removerItem({ id })),
                      aoAlterarObservacao: (id, observacao) => dispatch(definirObservacaoItem({ id, observacao })),
                    }}
                  />
                </Box>
              </Stack>

              <Paper
                elevation={0}
                square
                sx={{
                  flexShrink: 0,
                  width: deitado ? 'clamp(320px, 34%, 460px)' : '100%',
                  maxHeight: deitado ? undefined : '45%',
                  overflowY: deitado ? undefined : 'auto',
                  borderLeft: deitado ? 1 : 0,
                  borderTop: deitado ? 0 : 1,
                  borderColor: 'divider',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <TextField
                  label="Observação do pedido (opcional)"
                  value={observacaoGeral}
                  onChange={(e) => dispatch(definirObservacaoGeral(e.target.value))}
                  multiline
                  minRows={2}
                  fullWidth
                />

                <Box sx={{ flexGrow: 1 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                  <Typography variant="h3" color="success.main" sx={{ fontWeight: 'bold' }}>
                    Total
                  </Typography>

                  <Typography variant="h3" color="success.main" sx={{ fontWeight: 'bold' }}>
                    {formatarBRL(subtotal)}
                  </Typography>
                </Stack>

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={bloqueado}
                  onClick={aoFinalizar}
                  startIcon={<AddShoppingCartIcon />}
                  sx={{ ...ESTILO_ACAO_KIOSK, '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 28 } }}
                >
                  Finalizar pedido
                </Button>

                <Button
                  size="large"
                  variant="outlined"
                  startIcon={<AddIcon fontSize="large" />}
                  onClick={voltar}
                  sx={{ ...ESTILO_ACAO_KIOSK, '& .MuiButton-startIcon > *:nth-of-type(1)': { fontSize: 28 } }}
                >
                  Adicionar mais itens
                </Button>
              </Paper>
            </Stack>
          )}
        </Stack>
      </Dialog>
    </TemaKiosk>
  )
}
